import React, { useEffect, useMemo, useState, useCallback } from "react"
import {
  Button,
  Badge,
  UncontrolledTooltip,
  Row,
  Col,
  Progress,
  ButtonGroup,
} from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import VehicleContentsModal from "./VehicleContentsModal"
import ContentFilters from "./ContentFilters"
import StockIdCell from "components/Common/stock-table/StockIdCell"
import StockModelCell from "components/Common/stock-table/StockModelCell"
import StockPriceCell from "components/Common/stock-table/StockPriceCell"
import StockStatusCell from "components/Common/stock-table/StockStatusCell"
import ImageZipDownloader from "pages/StockPages/StockView/ImageZipDownloader"
import { useVehicleStockPublishes } from "hooks/useVehicleStockPublishes"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useResponsiveCollapse } from "hooks/useWindowSize"
import {
  calculatePublishPercentage,
  getCurrentContentCount,
} from "helpers/vehicle_stock_publish_helpers"

const VehicleStockPublishList = ({
  params,
  setPage,
  setSearch,
  updateParams,
  countsData,
}) => {
  const { platforms } = useFilteredVehicleOptions()
  const { isMobile, toggleRow, isExpanded } = useResponsiveCollapse()
  const [modal, setModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)

  const handleOpenModal = useCallback(
    (rowData, resetPage = true) => {
      setSelectedData(rowData)
      updateParams(prev => ({
        ...prev,
        p_modal: true,
        stock_id: rowData?.id,
        ...(resetPage && { p_page: 1 }),
      }))
      setModal(true)
    },
    [updateParams]
  )

  const handleCloseModal = useCallback(() => {
    setModal(false)
    setSelectedData(null)
    updateParams(prev => {
      const newParams = { ...prev }
      delete newParams.p_modal
      delete newParams.stock_id
      delete newParams.p_page
      return newParams
    })
  }, [updateParams])

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.slug && { slug: params.slug }),
      ...(params.search && { search: params.search }),
      pending: params.pending,
      ...(params.brand && { brand: params.brand }),
      ...(params.model && { model: params.model }),
      ...(params.year && { year: params.year }),
      ...(params.status && { status: params.status }),
      ...(params.location && { location: params.location }),
      ...(params.showroom && { showroom: params.showroom }),
      ...(params.is_published !== "" && params.is_published != null && { is_published: params.is_published }),
      ...(params.has_images !== "" && params.has_images != null && { has_images: params.has_images }),
      ...(params.has_price !== "" && params.has_price != null && { has_price: params.has_price }),
      ...(params.is_sold !== "" && params.is_sold != null && { is_sold: params.is_sold }),
      ...(params.price_min && { price_min: params.price_min }),
      ...(params.price_max && { price_max: params.price_max }),
      sort_by: params.sort_by || "created_at",
      sort_order: params.sort_order || "desc",
    }),
    [
      params.page, params.search, params.slug, params.pending,
      params.brand, params.model, params.year, params.status,
      params.location, params.showroom, params.is_published,
      params.has_images, params.has_price, params.is_sold, params.price_min,
      params.price_max, params.sort_by, params.sort_order,
    ]
  )

  const {
    data: publishResponse,
    isLoading,
    refetch: publishRefetch,
    isRefetching: publishRefetchLoading,
  } = useVehicleStockPublishes(queryParams)

  const columns = useMemo(
    () => [
      {
        header: "Stock ID",
        cell: ({ row }) => (
          <StockIdCell stock={row.original?.stock ?? {}} />
        ),
      },
      {
        header: "Model",
        cell: ({ row }) => (
          <StockModelCell
            stock={row.original?.stock ?? {}}
            rowId={row.id}
            isMobile={isMobile}
            toggleRow={toggleRow}
            isExpanded={isExpanded}
          />
        ),
      },
      {
        header: "Price",
        cell: ({ row }) => <StockPriceCell stock={row.original?.stock ?? {}} />,
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <StockStatusCell stock={row.original?.stock ?? {}} />
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const contents = row.original?.contents ?? []
          const summary = calculatePublishPercentage(contents)
          const stockId = row.original?.stock?.stock_id
          return (
            <>
              <Button
                color="primary"
                size="sm"
                onClick={() => handleOpenModal(row.original)}
              >
                View Contents
              </Button>
              <div className="mt-2">
                <Progress
                  value={summary.percentage}
                  color="info"
                  className="bg-light"
                  animated
                />
                <small>
                  {summary.published}/{summary.total}
                </small>
              </div>
              <div className="mt-2">
                <ImageZipDownloader
                  vehicleId={stockId}
                  btnText=""
                  loadingText="..."
                />
              </div>
            </>
          )
        },
      },
    ],
    [handleOpenModal, isMobile, toggleRow, isExpanded]
  )

  const publishData = publishResponse?.data?.data?.data || []
  const paginationInfo = publishResponse?.data?.data

  const currentPlatform =
    platforms?.find(item => item?.slug === params?.slug) || {}

  const currentPlatformCount = getCurrentContentCount(countsData, params?.slug)

  // Reopen modal if URL params say so
  useEffect(() => {
    if (params?.p_modal === "true" && params?.stock_id && !modal) {
      const foundItem = publishData.find(
        v => Number(v.id) === Number(params.stock_id)
      )
      if (foundItem) handleOpenModal(foundItem, false)
    }
  }, [params?.p_modal, params?.stock_id, publishData, handleOpenModal, modal])

  useEffect(() => {
    if (modal && selectedData?.id) {
      const updated = publishData.find(v => v.id === selectedData.id)
      if (updated && updated !== selectedData) {
        setSelectedData(updated) // 🔥 trigger re-render with new data
      }
    }
  }, [publishData, selectedData?.id, modal])

  return (
    <>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <h5 className="mb-0 me-auto">
          Content List{" "}
          <Badge color="info">{currentPlatform?.name ?? "-"}</Badge>
        </h5>
        <Button
          color="light"
          size="sm"
          onClick={publishRefetch}
          id="refreshTooltip"
        >
          <i
            className={`mdi mdi-refresh ${
              publishRefetchLoading ? "mdi-spin" : ""
            }`}
          />
          <UncontrolledTooltip
            placement="top"
            target="refreshTooltip"
            timeout={0}
          >
            Refresh
          </UncontrolledTooltip>
        </Button>
      </div>

      <Row className="mb-3 g-2">
        <Col xs={12} sm={5}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>

        <Col xs={12} sm={7} className="d-flex justify-content-sm-end align-items-center flex-wrap gap-2">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setIsFiltersOpen(prev => !prev)}
          >
            {isFiltersOpen ? "Hide Filters ▲" : "Show Filters ▼"}
          </button>
          <ButtonGroup size="sm">
            <Button
              color={params.p_all === "all" ? "primary" : "light"}
              onClick={() =>
                updateParams(prev => ({
                  ...prev,
                  pending: "",
                  p_all: "all",
                  page: 1,
                }))
              }
            >
              All{" "}
              <Badge color="info">
                {(currentPlatformCount?.total_pending ?? 0) +
                  (currentPlatformCount?.total_posted ?? 0)}
              </Badge>
            </Button>
            <Button
              color={
                params.pending === true || params.pending === "true"
                  ? "primary"
                  : "light"
              }
              onClick={() =>
                updateParams(prev => ({
                  ...prev,
                  pending: true,
                  p_all: "false",
                  page: 1,
                }))
              }
            >
              Pending{" "}
              <Badge color="info">
                {currentPlatformCount?.total_pending ?? 0}
              </Badge>
            </Button>
            <Button
              color={params.pending === "false" ? "primary" : "light"}
              onClick={() =>
                updateParams(prev => ({
                  ...prev,
                  pending: false,
                  p_all: "false",
                  page: 1,
                }))
              }
            >
              Posted{" "}
              <Badge color="info">
                {currentPlatformCount?.total_posted ?? 0}
              </Badge>
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      <ContentFilters
        params={params}
        updateParams={updateParams}
        isFiltersOpen={isFiltersOpen}
        isAdvancedOpen={isAdvancedOpen}
        setIsAdvancedOpen={setIsAdvancedOpen}
      />

      <TableContainer
        columns={columns}
        data={publishData}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || publishRefetchLoading}
      />

      {/* ✅ Global modal (not per row) */}
      <VehicleContentsModal
        modal={modal}
        toggle={handleCloseModal}
        selectedData={selectedData}
        params={params}
        updateParams={updateParams}
      />
    </>
  )
}

VehicleStockPublishList.displayName = "VehicleStockPublishList"
export default React.memo(VehicleStockPublishList)
